import React from 'react';
import {
    RibbonTopBar,
    type RibbonTabDefinitionBase,
    type RibbonTabDefinitionMLR,
    type RibbonTabDefinitionSLR,
    type RibbonTopBarProps,
    type TabHeader,
} from 'owa-acui';
import { appIcons, getAppStrings, getAppColors } from 'owa-acui-theme';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import { getCurrentThemeId, getIsLightBaseTheme, getIsDarkBaseTheme } from 'owa-theme';
import { ThemeConstants } from 'owa-theme-shared';
import { observer } from 'owa-mobx-react';
import classnames from 'owa-classnames';
import { topBar, topBarContainer, topBarWin32Solid } from './CommandRibbonTopBar.scss';
import {
    RIBBON_TAB_FONT_SIZE,
    RIBBON_TAB_FONT_WEIGHT,
    RIBBON_TAB_HORIZONTAL_PADDING,
    RIBBON_TAB_LINE_HEIGHT,
    RIBBON_TAB_MIN_HEIGHT,
} from '../util/ribbonTabStyleConstants';

export interface CommandRibbonTopBarProps {
    extraControlsAfterTabs?: React.ReactNode;
    extraControlsBeforeTabs?: React.ReactNode;
    onTabHeaderClick: (tabId: string) => void;
    ribbonTabs: RibbonTabDefinitionBase[];
    selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR;
}

/**
 * The Win32 styling overlay bleed-through treatment is only applied for the
 * Outlook (base) and Office Neutral themes.
 */
function isOutlookOrNeutralTheme(): boolean {
    const themeId = getCurrentThemeId();
    return (
        getIsLightBaseTheme() ||
        getIsDarkBaseTheme() ||
        themeId === ThemeConstants.OFFICE_NEUTRAL_THEME_ID ||
        themeId === ThemeConstants.MODERN_OFFICE_NEUTRAL_THEME_ID
    );
}

export const CommandRibbonTopBar: React.FunctionComponent<CommandRibbonTopBarProps> = observer(
    function CommandRibbonTopBar({
        extraControlsAfterTabs,
        extraControlsBeforeTabs,
        onTabHeaderClick,
        ribbonTabs,
        selectedTab,
    }: CommandRibbonTopBarProps) {
        return (
            <div
                id="TopBar"
                className={classnames(topBar, {
                    [topBarWin32Solid]: isWin32StylingEnabled() && isOutlookOrNeutralTheme(),
                })}
            >
                {extraControlsBeforeTabs}
                <div className={topBarContainer} onKeyDown={onKeyDown}>
                    <RibbonTopBar
                        appColors={getAppColors()}
                        appStrings={getAppStrings()}
                        customStyles={customTopBarStyles}
                        displayDynamicTabHeaderIndicator={true}
                        isTabIndicatorRounded={true}
                        onTabHeaderClick={onTabHeaderClick}
                        overflowTabHeaderMenuLauncherIcon={appIcons.tabListOverflowLauncher}
                        ribbonTabs={customizeTabStyles(ribbonTabs)}
                        selectedTabId={selectedTab.id}
                    />
                </div>
                {extraControlsAfterTabs}
            </div>
        );
    },
    'CommandRibbonTopBar'
);

/** Removing the default background color to support the bleed-through themes */
const customTopBarStyles: RibbonTopBarProps['customStyles'] = {
    root: {
        background: 'none',
    },
};

/** Removing the default background color from each tab to support the bleed-through themes */
const customizeTabStyles = (tabs: RibbonTabDefinitionBase[]): RibbonTabDefinitionBase[] => {
    const styles: TabHeader['styles'] = isWin32StylingEnabled()
        ? {
              wrapper: {
                  margin: 0,
              },
              ghostPivotLabel: {
                  paddingLeft: RIBBON_TAB_HORIZONTAL_PADDING,
                  paddingRight: RIBBON_TAB_HORIZONTAL_PADDING,
              },
              pivotHeader: {
                  root: {
                      background: 'none',
                      fontSize: RIBBON_TAB_FONT_SIZE,
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                      lineHeight: RIBBON_TAB_LINE_HEIGHT,
                      minHeight: RIBBON_TAB_MIN_HEIGHT,
                      padding: `0 ${RIBBON_TAB_HORIZONTAL_PADDING}`,
                  },
                  rootHovered: {
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                  },
                  rootPressed: {
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                  },
                  rootFocused: {
                      fontWeight: RIBBON_TAB_FONT_WEIGHT,
                  },
              },
          }
        : {
              pivotHeader: {
                  root: {
                      background: 'none',
                  },
              },
          };

    return tabs.map(tab => ({ ...tab, styles }));
};

const onKeyDown: React.KeyboardEventHandler = ({ key }) => {
    if (key === 'ArrowDown') {
        const firstBottomBarButton = document
            .getElementById('BottomBar')
            ?.querySelectorAll('button')[0];

        firstBottomBarButton?.focus();
    }
};
